import { Text, VStack } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'react-use'
import { useUserState } from '~/atoms/UserState'
import { SelectItem } from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ScrollingInputDropdown } from '~/components/shared/Dropdown/ScrollingInputDropdown'
import { ContactSearchListItem } from '~/components/specialized/ContactSearchInput/ContactSearchListItem'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useGetContacts, useGetContactSources } from '~/queries/contactsQueries'
import { AnalyticsNamespace, ContactsAnalyticsEvents } from '~/types/Analytics'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'
import { tryGetValueOrDefault } from '~/utils/common'
import { formatPhoneNumber } from '~/utils/formatter'

import { cleanDialstring } from '@mangovoice/core'
import { Contact, ContactSource, ContactSourceType } from '@mangovoice/types'

const SEARCH_DEBOUNCE_RATE_MS = 300
const SEARCH_CONTACTS_PAGE_SIZE = 25
const SEARCH_CACHE_TIME_MS = 60 * 1000 /* 1 minute */
const CONTACT_SOURCE_CACHE_TIME_MS = 10 * 60 * 1000 /* 10 minutes */

interface ContactSearchInputProps {
  scope: string
  contactSourceTypeFilter?: ContactSourceType[]
  isInvalid?: boolean
  value?: string
  onValueChanged?: (value: string) => void
}

type ContactLabelValue = {
  phoneNumber: string
  contactName: string
  isExtension: boolean
}
interface ContactLabelData {
  value: ContactLabelValue
  node: React.ReactNode
}
const getContactLabelData = (
  contacts?: Contact[],
  contactSources?: ContactSource[],
  sourceTypeFilter?: ContactSourceType[],
): ContactLabelData[] => {
  if (!contacts) {
    return []
  }

  const labels = contacts.map((contact) => {
    if (!contact.phone_numbers) {
      return []
    }

    if (sourceTypeFilter && sourceTypeFilter.length > 0) {
      const isSourceFiltered = contactSources?.some(
        (cs) =>
          cs.id === contact.contact_source_id &&
          sourceTypeFilter.includes(cs.source),
      )
      if (isSourceFiltered) {
        return []
      }
    }

    const extensionSourceId = contactSources?.find(
      (cs) => cs.source === ContactSourceType.Extensions,
    )?.id

    return contact.phone_numbers?.map((phoneNumber) => {
      const isExtension = contact.contact_source_id === extensionSourceId
      return {
        value: {
          phoneNumber: phoneNumber.number,
          contactName: `${contact.given_name} ${contact.family_name}`,
          isExtension: Boolean(isExtension),
        },
        node: (
          <MenuListItem>
            <ContactSearchListItem
              contact={contact}
              contactPhoneNumber={phoneNumber}
              isExtension={isExtension}
            />
          </MenuListItem>
        ),
      } as ContactLabelData
    })
  })

  return labels.flat()
}

export const ContactSearchInput = ({
  scope,
  contactSourceTypeFilter,
  isInvalid,
  value,
  onValueChanged,
}: ContactSearchInputProps) => {
  const [isFocused, setIsInputFocused] = useState<boolean>(false)
  const [canSignalInvalid, setCanSignalInvalid] = useState<boolean>(false)
  const [contactLabelData, setContactLabelData] = useState<ContactLabelData[]>(
    [],
  )
  const [inputValue, setInputValue] = useState('')
  const [unformattedInputValue, setUnformattedInputValue] = useState<string>('')
  const [searchValue, setSearchValue] = useState({ term: '' })
  const [pageData, setPageData] =
    useState<ReturnType<typeof getPaginatedResultParts>>()
  const { pbxId } = useUserState()
  const { t } = useTranslation()

  if (value && value !== inputValue) {
    setInputValue(value)
  }

  const analytics = useAnalytics(AnalyticsNamespace.CONTACTS)

  const { data: contactSources } = useGetContactSources({
    pbxId: pbxId ?? 0,
    options: {
      enabled: true,
      staleTime: CONTACT_SOURCE_CACHE_TIME_MS,
      retry: 3,
    },
  })

  const {
    refetch: getContactsPage,
    isFetching: isContactsListFetching,
    tryCancelQuery: cancelContactsSearch,
  } = useGetContacts({
    pbxId: pbxId ?? 0,
    options: {
      staleTime: SEARCH_CACHE_TIME_MS,
    },
    onQuerySuccess(data) {
      const parts = getPaginatedResultParts(data)
      setPageData(parts)
      if (parts) {
        const contactLabels = getContactLabelData(
          parts?.results,
          contactSources?.results,
          contactSourceTypeFilter,
        )
        setContactLabelData((prev) => [...prev, ...contactLabels])
      }
    },
  })

  const handleFetchNextPage = () => {
    //don't fetch if we are already fetching or there are no more pages
    if (isContactsListFetching || !pageData?.hasNextPage) {
      return
    }

    getContactsPage({
      offset: pageData?.nextPageOffset ?? 0,
      limit: SEARCH_CONTACTS_PAGE_SIZE,
      ordering: ['given_name', 'family_name'],
      search: searchValue.term,
    })
  }

  const resetSearchState = () => {
    setContactLabelData([])
    setPageData(undefined)
  }

  const [, cancelDebounce] = useDebounce(
    () => {
      const trimmed = unformattedInputValue.trim()
      setSearchValue({ term: trimmed })
    },
    SEARCH_DEBOUNCE_RATE_MS,
    [unformattedInputValue],
  )

  //on mount, don't update search value
  useEffect(() => {
    cancelDebounce()

    return () => {
      cancelDebounce()
    }
  }, [])

  //on search value change, fetch contacts
  useEffect(() => {
    analytics.track(ContactsAnalyticsEvents.ContactSearchInputSearched, {
      scope,
      searchValue,
    })

    getContactsPage({
      offset: 0,
      limit: SEARCH_CONTACTS_PAGE_SIZE,
      search: searchValue.term,
    })
  }, [searchValue])

  const handleFocus = (focused: boolean) => {
    setIsInputFocused(focused)

    if (!focused) {
      setCanSignalInvalid(true)
    }
  }

  const handleValueChanged = (value: string, unformattedOverride?: string) => {
    cancelContactsSearch()
    cancelDebounce()
    resetSearchState()

    //try and format the input - if it's a valid number, it will be formatted
    const formattedValue = tryGetValueOrDefault(formatPhoneNumber(value), value)

    setInputValue(formattedValue!)
    onValueChanged?.(formattedValue!)

    const unformatted =
      unformattedOverride ??
      (formattedValue
        ? tryGetValueOrDefault(cleanDialstring(formattedValue), value)
        : value)

    setUnformattedInputValue(unformatted ?? '')
  }

  const handleSelected = (value: ContactLabelValue) => {
    const labelData = value
    const contactPhone = labelData.phoneNumber
    const cleanedPhone = cleanDialstring(contactPhone)

    const unformattedOverride = labelData.isExtension
      ? labelData.contactName
      : undefined

    handleValueChanged(cleanedPhone, unformattedOverride)
    if (labelData.isExtension) {
      analytics.track(
        ContactsAnalyticsEvents.ContactSearchInputExtensionClicked,
      )
    } else {
      analytics.track(
        ContactsAnalyticsEvents.ContactSearchInputPhoneNumberClicked,
      )
    }
  }

  const shouldBeOpen = isFocused && contactLabelData.length > 0
  const isToInvalid = canSignalInvalid && isInvalid
  return (
    <VStack spacing="0.375rem" align="stretch" width="100%">
      <ScrollingInputDropdown
        items={contactLabelData.map((data) => {
          return {
            value: data.value,
            content: data.node,
          } as SelectItem<ContactLabelValue>
        })}
        isOpen={shouldBeOpen}
        value={inputValue}
        onMenuClose={() => handleFocus(false)}
        onFocus={() => handleFocus(true)}
        onScrollEndReached={handleFetchNextPage}
        autoCloseOnBlur
        autoCloseOnSelected
        onSelected={handleSelected}
        onValueChanged={handleValueChanged}
        disabled={!contactSources}
        placeholder={t('contactSearchInput.placeholder')}
        isInvalid={isToInvalid}
        isLoading={isContactsListFetching}
      />
      {isToInvalid && isInvalid && (
        <Text variant="error">
          {t('contactSearchInput.invalidPhoneNumber')}
        </Text>
      )}
    </VStack>
  )
}
